import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation, useParams } from 'react-router-dom';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Paper, Typography } from '@mui/material';

import { toTitleCase } from '~/utils/toTitleCase';

import {
  AssetMetadata,
  VideoMetadata,
  getAssetsSignedUrls,
  getOutputVideosSignedUrls,
  search,
} from '../../services/VideoGenService';

export default function MediaPreviewPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const location = useLocation();
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [metadata, setMetadata] = useState<AssetMetadata | VideoMetadata | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const isAsset = location.pathname.includes('/asset/');

  const handleCopy = (value: unknown, key: string) => {
    const textToCopy = typeof value === 'object' ? JSON.stringify(value) : String(value);
    navigator.clipboard.writeText(textToCopy);
    setCopiedKey(key);
    enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  useEffect(() => {
    const fetchSignedUrl = async () => {
      try {
        if (!videoId) {
          return;
        }

        const response = await (isAsset
          ? getAssetsSignedUrls({ assetIds: [videoId] })
          : getOutputVideosSignedUrls({ videoIds: [videoId] }));
        setVideoUrl(response.result[videoId]);
      } catch (err) {
        enqueueSnackbar('Failed to load video', { variant: 'error' });
        setError('Failed to load video');
        console.error('Error fetching signed URL:', err);
      }
    };

    const fetchMetaData = async () => {
      if (!videoId) {
        return;
      }
      try {
        const response = await search(isAsset ? 'assets' : 'videos', { ids: [videoId] });
        if (response.result && response.result.length > 0) {
          setMetadata(response.result[0]);
        }
      } catch (err) {
        setError('Failed to load metadata');
        console.error('Error fetching metadata:', err);
      }
    };

    fetchSignedUrl();
    fetchMetaData();
  }, [videoId, isAsset]);

  const shouldShowVideo = metadata && (metadata.status === 'ACTIVE' || metadata.status === 'SUCCEEDED');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        height: '100%',
        maxWidth: 1000,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Paper elevation={3} sx={{ boxShadow: 3 }}>
        {error ? (
          <Box
            sx={{
              backgroundColor: 'grey.100',
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        ) : shouldShowVideo && videoUrl ? (
          <Box
            component="video"
            src={videoUrl}
            controls
            preload="metadata"
            sx={{
              backgroundColor: 'grey.100',
              maxHeight: 700,
              width: '100%',
            }}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: 'grey.100',
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {metadata?.status || 'Loading video...'}
            </Typography>
          </Box>
        )}
        <Box sx={{ p: 2 }}>
          {metadata && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {isAsset ? 'Asset Details' : 'Output Video Details'}
              </Typography>
              {Object.entries(metadata).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium', minWidth: 150 }}>
                    {toTitleCase(key)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <IconButton size="small" onClick={() => handleCopy(value, key)} sx={{ p: 0.5, mr: 1 }}>
                      {copiedKey === key ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </IconButton>
                    <Typography variant="caption" sx={{ color: 'text.primary' }}>
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
